import React, { useMemo } from "react";
import {
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HomeIcon from "@mui/icons-material/Home";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LogoutIcon from "@mui/icons-material/Logout";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings } from "@mui/icons-material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  onClick?: () => void;
}

interface PopoverMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isFieldOperator?: boolean;
  logout: () => void;
}

const PopoverMenu: React.FC<PopoverMenuProps> = ({
  anchorEl,
  open,
  onClose,
  isAuthenticated,
  isAdmin,
  isFieldOperator,
  logout,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems: MenuItem[] = useMemo(() => {
    if (!isAuthenticated) {
      return [
        { text: t("sidebar.login"),           icon: <LoginIcon />,              path: "/login" },
        { text: t("sidebar.signUp"),           icon: <PersonAddIcon />,          path: "/register" },
        { text: t("sidebar.registerAsAPro"),   icon: <DesignServicesIcon />,     path: "/service-provider/register" },
      ];
    }

    return [
      // Home is the public home page for everyone; staff reach their page through "Admin Home" (PM 2026-10-24)
      { text: t("sidebar.home"),               icon: <HomeIcon />,               path: "/" },
      { text: t("sidebar.myAccount"),          icon: <ManageAccountsIcon />,     path: "/account/edit" },
      // "Admin Home" (staff quick access page) above App Administration; internal, English only (PM 2026-10-24)
      ...(isAdmin
        ? [{ text: "Admin Home", icon: <AdminPanelSettingsIcon />, path: "/admin/home" },
           { text: t("sidebar.appAdministration"), icon: <Settings />, path: "/admin/dashboard" }]
        : isFieldOperator
          ? [{ text: "Admin Home", icon: <AdminPanelSettingsIcon />, path: "/field-operator/home" },
             { text: t("sidebar.appAdministration"), icon: <Settings />, path: "/field-operator/dashboard" }]
          : []),
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin, isFieldOperator, t]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            width: 240,
            borderRadius: 3,
            p: 1,
          },
        },
      }}
    >
      <List
        sx={{
          "& .MuiListItemIcon-root": {
            color: "primary.main",
            minWidth: 40,
          },
          "& .MuiListItemText-primary": {
            color: "primary.main",
            fontSize: "16px",
          },
        }}
      >
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            selected={item.path ? location.pathname === item.path : false}
            onClick={async () => {
              if (item.onClick) {
                item.onClick();
              }
              if (item.path) {
                navigate(item.path);
                onClose();
              }
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}

        {isAuthenticated && (
          <>
            <Divider sx={{ my: 1 }} />

            <ListItemButton
              onClick={() => {
                logout();
                onClose();
              }}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t("sidebar.logout")} />
            </ListItemButton>
          </>
        )}
      </List>
    </Popover>
  );
};

export default PopoverMenu;
